import React, { useState, useEffect } from 'react';
import { safariTips } from './LoadingState.messages';

interface LoadingStateProps {
  message?: string;
  className?: string;
  type?: 'safari' | 'trip' | 'general' | 'auth';
}

export const LoadingState: React.FC<LoadingStateProps> = ({
  type = 'general',
}) => {
  // Safari-themed loading messages
  const safariLoadingMessages = [
    'Tracking majestic wildlife across the Serengeti...',
    'Discovering hidden gems in the African wilderness...',
    'Coordinating with expert local safari guides...',
    'Checking availability of luxury safari lodges...',
    'Mapping out the perfect migration viewing spots...',
    'Preparing your unforgettable safari experience...',
    'Scouting the best photography locations...',
    'Finding the most scenic camping locations...',
    'Ensuring your adventure is perfectly planned...',
    'Connecting with verified tour operators...',
    'Loading breathtaking safari destinations...',
    'Gathering the latest wildlife sighting reports...',
  ];

  // Trip-themed loading messages
  const tripLoadingMessages = [
    'Finding the perfect transportation routes...',
    'Checking vehicle availability and schedules...',
    'Coordinating with experienced drivers...',
    'Mapping out scenic travel routes...',
    'Ensuring comfortable journey experiences...',
    'Connecting with reliable transport operators...',
    'Verifying vehicle safety and maintenance...',
    'Planning optimal pickup and drop-off points...',
    'Securing the best travel deals for you...',
    'Loading available trip options...',
    'Checking real-time availability...',
    'Preparing your travel itinerary...',
  ];

  // General loading messages
  const generalLoadingMessages = [
    'Loading your adventure options...',
    'Preparing amazing experiences...',
    'Gathering the latest information...',
    'Connecting to our services...',
    'Processing your request...',
    'Almost ready for you...',
  ];

  // Auth loading messages
  const authLoadingMessages = [
    'Tracking the herd...',
    'Packing the jeep...',
    'Spotting lions...',
    'Crossing the savannah...',
    'Scouting for adventure...',
    'Kicking up safari dust...',
    'Binoculars up...',
    'Following elephant tracks...',
    'Setting up camp...',
    'Roaring into action...',
  ];

  // Select appropriate messages based on type
  const getMessages = () => {
    switch (type) {
      case 'safari':
        return safariLoadingMessages;
      case 'trip':
        return tripLoadingMessages;
      case 'auth':
        return authLoadingMessages;
      default:
        return generalLoadingMessages;
    }
  };

  const messages = getMessages();
  const [currentMessageIndex, setCurrentMessageIndex] = useState(() =>
    Math.floor(Math.random() * messages.length)
  );

  const tripTips = [
    '\uD83D\uDCA1 Tip: Book your transportation early for the best rates and availability.',
    '\uD83D\uDCA1 Tip: Double-check your pickup location and time.',
    '\uD83D\uDCA1 Tip: Pack light for easier travel.',
    '\uD83D\uDCA1 Tip: Stay hydrated during your journey.',
    '\uD83D\uDCA1 Tip: Confirm your booking details before departure.',
    '💧 Travel Tip: Always carry a reusable water bottle to stay hydrated on your adventures.',
    '📸 Pack light, but don\'t forget your camera—memories are priceless!',
  ];

  const getTips = () => {
    switch (type) {
      case 'safari':
        return safariTips;
      case 'trip':
        return tripTips;
      default:
        return [];
    }
  };

  const tips = getTips();
  const [tip] = useState(() => tips[Math.floor(Math.random() * tips.length)]);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentMessageIndex(prev => (prev + 1) % messages.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [messages.length]);

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-white z-50">
      <div className="flex flex-col items-center">
        <div className="w-16 h-16 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin" />
        <p className="mt-4 text-gray-600 text-sm">
          {messages[currentMessageIndex]}
        </p>
        {tips.length > 0 && (
          <>
            <div className="w-8 h-px bg-gray-200 mt-3 mb-2" />
            <p className="mt-2 text-xs text-gray-500 max-w-xs text-center">
              {tip}
            </p>
          </>
        )}
      </div>
    </div>
  );
};
